/**
 * Multimedia Grid
 */
import { Component, registerComponent } from 'js/component.js';
import { isEditModeFooter, getViewportSize } from 'js/helpers.js';
const componentName = 'multimedia-grid';

class MultimediaGrid extends Component {

    /**
     * Component Setup
     */

    // Elements
    elements = {
        container: this.element.querySelector(`.${componentName}__container`),
        thumbnailList: this.element.querySelector(`.${componentName}__thumbnail-list`),
        thumbnails: this.element.getElementsByClassName(`${componentName}__thumbnail`),
        activeMediaParent: this.element.querySelector(`.${componentName}__active-media`),
        prevButton: this.element.querySelector(`.${componentName}__previous`),
        nextButton: this.element.querySelector(`.${componentName}__next`),
        mobileSlider: this.element.querySelector(`.${componentName}__mobile-slider`)
    };

    // Util & State
    currentViewport = getViewportSize();

    sliderPositionX = 0;
    totalSlides = this.elements.thumbnails.length;

    getThumbnailHeight = () => {
        return (this.elements.thumbnails?.[0]?.offsetHeight ?? 84) + 8;
    }

    /**
     * Populate Active Media
     * @param { DOM Element } thumbnailEle - thumbnail element to show the selected active media
     */
    populateActiveMedia = (thumbnailEle) => {
        const slideId = thumbnailEle.getAttribute('data-slide');

        // Hide all active media elements
        const activeMediaElements = this.elements.container.querySelectorAll(`.${componentName}__active-media`);
        activeMediaElements.forEach((element) => element.classList.add('hidden'));

        // Show the selected media element
        const targetElement = this.element.querySelector(`[id="${slideId}"]`);
        if (targetElement) {
            targetElement.classList.remove('hidden');
        }

        // Recalibrate video size
        if (typeof (Event) === 'function') {
            // modern browsers
            window.dispatchEvent(new Event('resize'));
        } else {
            // for IE and other old browsers
            // causes deprecation warning on modern browsers
            const evt = window.document.createEvent('UIEvents');
            evt.initUIEvent('resize', true, false, window, 0);
            window.dispatchEvent(evt);
        }
    };

    /**
     * Update Desktop Carousel
     * @param { String } direction - pass in 'previous' or 'next' to transition slides
     */
    updateDesktopCarousel = (direction) => {
        const desktopSlides = 4;
        if(this.totalSlides > desktopSlides) {
            const maxTransition = -((this.totalSlides - desktopSlides) * this.getThumbnailHeight());
            const prevTransition = this.sliderPositionX + this.getThumbnailHeight();
            const nextTransition = this.sliderPositionX - this.getThumbnailHeight();

            // Update Direction if passed in
            if(direction) {
                this.sliderPositionX = direction === 'next' ? nextTransition : prevTransition;
                this.elements.thumbnailList.style.transform = `translateY(${this.sliderPositionX}px)`;
            }

            // Update Show/Hide of Buttons
            this.elements.prevButton.classList.remove('active');
            this.elements.nextButton.classList.remove('active');

            if(this.sliderPositionX === 0) this.elements.nextButton.classList.add('active');
            if(this.sliderPositionX < 0) {
                this.elements.prevButton.classList.add('active');
                this.elements.nextButton.classList.add('active');
            }
            if(this.sliderPositionX === maxTransition) this.elements.nextButton.classList.remove('active');
        }
    };

    /**
     * On Thumbnail Click (Desktop)
     */
    onThumbnailClick = (e) => {
        const thumbnailEle = e.currentTarget;
        if(!thumbnailEle.classList.contains('active') && e.isTrusted) {
            [...this.elements.thumbnails].forEach((ele) => ele.classList.remove('active'));
            this.populateActiveMedia(thumbnailEle);
            thumbnailEle.classList.add('active');
        }
    };

    /**
     * Add Event Listeners
     */
    addEventListeners = () => {
        // Component Listeners
        [...this.elements.thumbnails].forEach((ele) => ele.addEventListener('click', (e) => {
            if(this.currentViewport === 'desktop') this.onThumbnailClick(e);
        }));
        this.elements.prevButton.addEventListener('click', () => this.updateDesktopCarousel('previous'));
        this.elements.nextButton.addEventListener('click', () => this.updateDesktopCarousel('next'));
        if (this.totalSlides > 5) {
            this.elements.nextButton.classList.add('active');
        }
    };

    /**
     * Initialize Component
     */
    init() {
        // Remove default carousel class on load
        this.element.classList.remove('carousel');

        // Fire Init Funcs
        if(!isEditModeFooter() && this.elements.thumbnails.length > 0) {
            this.addEventListeners();
        }

        if (this.elements.container.querySelector('.video')) {
            window.dispatchEvent(new CustomEvent('init-video-players')); // Trigger video initialization
        }
    }
}

registerComponent(`.${componentName}`, MultimediaGrid);
